import { Injectable } from '@angular/core';
import { AppService } from './app.service';
import { SearchType } from './search.types';

@Injectable({
  providedIn: 'root'
})
export class InlineSearchService {
  defaultOptions = {
    allowClear: false,
    autoSearch: false,
    allowMultiSelect: false,
    maxRows: 100,
    maxRowLimiter: true,
    modelProperty: '',
    descriptionProperty: ''
  };

  constructor(private app: AppService) {}

  public getDefaultOptions(options: any, objectType: SearchType, formControl: string, descFormControl: string): any {
    let providedOptions: any = {};

    if (options && typeof options === 'object') {
      for (let key in options) {
        if (options.hasOwnProperty(key)) {
          if (key === 'canClear' || key === 'allowClear') {
            providedOptions['allowClear'] = options[key];
          } else if (key === 'canMultiSelect' || key === 'allowMultiSelect') {
            providedOptions['allowMultiSelect'] = options[key];
          } else {
            providedOptions[key] = options[key];
          }
        }
      }
    }

    let defaultSearchOptions: any = {
      ...this.defaultOptions,
      ...providedOptions
    };

    let formProperties = this.getFormProperties(objectType, formControl, descFormControl);
    defaultSearchOptions.modelProperty = formProperties.modelProperty;
    defaultSearchOptions.descriptionProperty = formProperties.descriptionProperty;

    return defaultSearchOptions;
  }

  public getFormProperties(objectType: SearchType, modelFormControl: string, descFormControl: string): any {
    let formProps: any = {
      modelProperty: '',
      descriptionProperty: ''
    };

    switch (objectType) {
      case SearchType.PartyAgreement:
      case SearchType.Broker:
      case SearchType.Employee:
      case SearchType.Supplier:
      case SearchType.Provider:
      case SearchType.Entity:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Entity_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Entity_Name';
        break;
      case SearchType.Agreement:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Agmt_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Agmt_Num';
        break;
      case SearchType.ClassCode:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Class_Code';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Class_Rating_Desc';
        break;
      case SearchType.Claim:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Claim_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Book_Claim_Num';
        break;
      case SearchType.Condition:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : '';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : '';
        break;
      case SearchType.Contract:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Contract_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Conract_Num';
        break;
      case SearchType.ContractProfile:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : '';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : '';
        break;
      case SearchType.Document:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Doc_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Doc_Name';
        break;
      case SearchType.Form:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : '';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : '';
        break;
      case SearchType.TeamSet:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Team_Set_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Team_Set_Name';
        break;
      case SearchType.Workgroup:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Workgroup_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Workgroup_Name';
        break;
      case SearchType.IntegrationServiceConfig:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Service_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Service_Name';
        break;
      case SearchType.IntegrationActionConfig:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Action_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Action_Name';
        break;
      case SearchType.CodeSet:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Code';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Code_Desc';
        break;
      case SearchType.Invoice:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Invoice_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Invoice_Num';
        break;
      case SearchType.Statement:
        formProps.modelProperty = modelFormControl ? modelFormControl.split('.').slice(-1)[0] : 'Doc_ID';
        formProps.descriptionProperty = descFormControl ? descFormControl.split('.').slice(-1)[0] : 'Doc_ID';
        break;
      default:
        console.error(`Unknown search type passed to ep-inline-search. Type=${objectType}`);
        return;
    }

    return formProps;
  }

  public getForcedSearchResultProperties(searchType: SearchType) {
    let resultProps = {
      modelProperty: '',
      descriptionProperty: ''
    };

    switch (searchType) {
      case SearchType.ClassCode:
        resultProps.modelProperty = 'Class_Code';
        resultProps.descriptionProperty = 'Class_Code_Desc';
        break;

      case SearchType.Contract:
        resultProps.modelProperty = 'Contract_ID';
        resultProps.descriptionProperty = 'Contract_Num';
        break;
    }

    return { ...resultProps };
  }

  public getSearchType(objectType: string, roleType: string): string {
    let searchType: string;
    if (objectType == 'partyagreement') {
      if (roleType) {
        if (roleType == 'BK') {
          searchType = 'BrokerAgreementTerm';
        } else if (roleType == 'SP') {
          searchType = 'SupplierAgreementTerm';
        }
      }
    } else if (objectType == 'codeset') {
      searchType = 'CodeSet';
    } else {
      searchType = this.capitalizeFirstLetter(objectType);
    }

    return searchType;
  }

  public setInlineSearchCriteria(criteria: any, options: any, objectType: SearchType, searchText: string) {
    let searchCriteria: any = {};
    const searchType = objectType;

    switch (searchType) {
      case SearchType.Agreement:
        searchCriteria = this.setAgreementCriteria(criteria, options, searchText);
        break;
      case SearchType.PartyAgreement:
        searchCriteria = this.setPartyAgreementCriteria(criteria, options, searchText);
        break;
      case SearchType.Claim:
        searchCriteria = this.setClaimCriteria(criteria, options, searchText);
        break;
      case SearchType.ClassCode:
        searchCriteria = this.setClassCodeCriteria(criteria, options, searchText);
        break;
      case SearchType.Condition:
        searchCriteria = this.setConditionCriteria(criteria, options, searchText);
        break;
      case SearchType.Contract:
        searchCriteria = this.setContractCriteria(criteria, options, searchText);
        break;
      case SearchType.ContractProfile:
        searchCriteria = this.setContractProfileCriteria(criteria, options, searchText);
        break;
      case SearchType.Document:
        searchCriteria = this.setDocumentCriteria(criteria, options, searchText);
        break;
      case SearchType.Employee:
        searchCriteria = this.setEmployeeCriteria(criteria, options, searchText);
        break;
      case SearchType.Entity:
        searchCriteria = this.setEntityCriteria(criteria, options, searchText);
        break;
      case SearchType.Broker:
        searchCriteria = this.setEntityCriteria(criteria, options, searchText);
        break;
      case SearchType.Supplier:
        searchCriteria = this.setEntityCriteria(criteria, options, searchText);
        break;
      case SearchType.Provider:
        searchCriteria = this.setEntityCriteria(criteria, options, searchText);
        break;
      case SearchType.Form:
        searchCriteria = this.setFormCriteria(criteria, options, searchText);
        break;
      case SearchType.TeamSet:
        searchCriteria = this.setTeamSetCriteria(criteria, options, searchText);
        break;
      case SearchType.Workgroup:
        searchCriteria = this.setWorkgroupCriteria(criteria, options, searchText);
        break;
      case SearchType.IntegrationServiceConfig:
        searchCriteria = this.setServiceCriteria(criteria, options, searchText);
        break;
      case SearchType.IntegrationActionConfig:
        searchCriteria = this.setActionCriteria(criteria, options, searchText);
        break;
      case SearchType.CodeSet:
        searchCriteria = this.setCodeSetCriteria(criteria, options, searchText);
        break;
      case SearchType.Invoice:
        searchCriteria = this.setInvoiceCriteria(criteria, options, searchText);
        break;
      case SearchType.Statement:
        searchCriteria = this.setStatementCriteria(criteria, options, searchText);
        break;
      default:
        console.error(`Unknown search type passed to ep-search-inline. Type=${searchType}`);
        return;
    }

    return searchCriteria;
  }

  private setAgreementCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };
    searchCriteria.View = 'Full';
    searchCriteria.Procedure_Name = 'epx_tempopenagreementsearch';

    searchCriteria.Agmt_Num = searchText;
    return searchCriteria;
  }

  private setPartyAgreementCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    if (criteria.Role_Type && criteria.Role_Type > ' ') {
      switch (criteria.Role_Type) {
        case 'BK':
          searchCriteria.objectType = 'BrokerAgreementTerm';
          break;
        case 'SP':
          searchCriteria.objectType = 'SupplierAgreementTerm';
          break;
      }
    }

    searchCriteria.BookEntityID = criteria.BookEntityID ? criteria.BookEntityID : 'CURRENTBOOK';
    searchCriteria.View = 'Standard';
    searchCriteria.Entity_Name_Mode = 'CONTAINSALLWORDS';
    searchCriteria.Entity_Name = searchText;

    return searchCriteria;
  }

  private setClaimCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Claim';
    searchCriteria.Max = options.maxRows;
    searchCriteria.json = 'CT2';
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Book_Claim_Num = searchText;

    return searchCriteria;
  }

  private setConditionCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Condition';
    searchCriteria.Condition_Name = searchText;
    searchCriteria.Condition_Name_Mode = 'Beginswith';

    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }

    return searchCriteria;
  }

  private setContractCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Contract';
    searchCriteria.Max = options.maxRows;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Contract_Num = searchText;

    return searchCriteria;
  }

  private setContractProfileCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'DBQuery';
    searchCriteria.Procedure_Name = 'epx_tempcontractprofilesearch';

    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }

    return searchCriteria;
  }

  private setDocumentCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    return searchCriteria;
  }

  private setEmployeeCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Employee';
    searchCriteria.Max = options.maxRows;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Entity_Name = searchText;

    return searchCriteria;
  }

  private setEntityCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Entity';

    if (criteria.Role_Type && criteria.Role_Type > ' ') {
      switch (criteria.Role_Type) {
        case 'CN':
          searchCriteria.Prospect_Ind = 'Y;N';
          searchCriteria.Role_Type = 'CN';
          break;
        case 'CN-A':
          searchCriteria.Prospect_Ind = 'Y;N';
          searchCriteria.Role_Type = 'CN-A';
          break;
        case 'BK':
          searchCriteria.Role_Type = 'BK';
          break;
        case 'BK-A':
          searchCriteria.Role_Type = 'BK-A';
          break;
        case 'EM':
          searchCriteria.Role_Type = 'EM';
          break;
        case 'EM-A':
          searchCriteria.Role_Type = 'EM-A';
          break;
        case 'FC':
          searchCriteria.Role_Type = 'FC';
          break;
        case 'FC-A':
          searchCriteria.Role_Type = 'FC-A';
          break;
        case 'PR':
          searchCriteria.Role_Type = 'PR';
          break;
        case 'SP':
          searchCriteria.Role_Type = 'SP';
          break;
        case 'SP-A':
          searchCriteria.Role_Type = 'SP-A';
          break;
        case 'TP':
          searchCriteria.Role_Type = 'TP';
          break;
        case 'TP-A':
          searchCriteria.Role_Type = 'TP-A';
          break;
        case 'AF':
          searchCriteria.Role_Type = 'BK-A;CN-A;SP-A;EM-A;CL-A;BO-A;TP-A;TA-A;FC-A;PR-A';
          break;
        case 'TA':
          searchCriteria.Role_Type = 'TA';
          break;
        case 'CL':
          searchCriteria.Role_Type = 'CL';
          break;
        case 'NO-AF':
          searchCriteria.Role_Type = 'BK;CN;SP;EM;CL;BO;TP;TA;FC;PR'; //excludes affiliates
          break;
      }
    }

    searchCriteria.Max = options.maxRows;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Entity_Name = searchText;

    return searchCriteria;
  }

  private setFormCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Form';
    searchCriteria.Form_Num = searchText;
    searchCriteria.Form_Num_Mode = 'Beginswith';

    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }

    return searchCriteria;
  }

  private setTeamSetCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    return searchCriteria;
  }

  private setWorkgroupCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'Workgroup';
    searchCriteria.Disabled_Ind = 'N';
    searchCriteria.Max = options.maxRows;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Workgroup_Name = searchText;

    return searchCriteria;
  }
  private setServiceCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'IntegrationServiceConfig';
    searchCriteria.Disabled_Ind = 'N';
    searchCriteria.Max = options.maxRows;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Service_Name = searchText;

    return searchCriteria;
  }
  private setActionCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'IntegrationActionConfig';
    searchCriteria.Disabled_Ind = 'N';
    searchCriteria.Max = options.maxRows;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }
    searchCriteria.Action_Name = searchText;

    return searchCriteria;
  }

  private setCodeSetCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };

    searchCriteria.objectType = 'CodeSet';
    searchCriteria.Disabled_Ind = 'N';
    searchCriteria.Max = 500;
    if (!criteria.View) {
      searchCriteria.View = 'Full';
    }

    searchCriteria.Code_Desc = searchText;

    return searchCriteria;
  }

  private setInvoiceCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };
    searchCriteria.View = 'Full';
    searchCriteria.Procedure_Name = 'epx_tempopeninvoicesearch';

    if (isNaN(Number(searchText))) {
      searchText = null;
    }
    searchCriteria.Invoice_Num = searchText;
    return searchCriteria;
  }

  private setStatementCriteria(criteria: any, options: any, searchText: string) {
    let searchCriteria: any = { ...criteria };
    searchCriteria.View = 'Full';
    searchCriteria.Procedure_Name = 'epx_tempstatementsearch';

    if (isNaN(Number(searchText))) {
      searchText = null;
    }
    searchCriteria.Doc_ID = searchText;
    return searchCriteria;
  }

  private setClassCodeCriteria(criteria: any, options: any, searchText?: string) {
    let searchCriteria: any = { ...criteria };
    searchCriteria.View = 'Full';
    searchCriteria.Procedure_Name = 'epx_getclasscode';

    return searchCriteria;
  }

  private capitalizeFirstLetter(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}
